/**
 * socket.service.ts
 * Secured Communications
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
 */

import bluebird from "bluebird";

import { MeetingMessageFrame, MessageFrame, MessageFrameType } from "../models/message";
import { deserialize } from "bson";
import { http, HttpService } from "./http.service";
import { UnauthError } from "../models/error";


export interface MessageDelegate {
  onOpen(): Promise<void>,
  processMessage(msg: MessageFrame | MeetingMessageFrame): Promise<void>,
  onClose(): void
}

export interface SocketService {
  deviceID: string | null,
  msgDelegate?: MessageDelegate,
  readonly running: boolean;
  start(): void,
  restart(): void,
  stop(): void,
  send(msg: string | ArrayBuffer): void
}

const INIT_TIME = 5000;

class SocketServiceImpl implements SocketService {

  public deviceID: string | null;
  protected socket: WebSocket | null;
  protected shouldRetry: boolean;
  protected closed: Promise<boolean> | null;
  //allow users of this to inject their own http service
  private httpService: HttpService | undefined;

  public msgDelegate?: MessageDelegate

  get running(): boolean {
    return this.socket !== null;
  }

  constructor(httpService?: HttpService) {
    this.deviceID = null;
    this.socket = null;
    this.shouldRetry = false;
    this.closed = null;
    this.httpService = httpService;
  }

  start() {
    this.shouldRetry = true;
    this.run();
  }

  restart() {
    this.shouldRetry = false;
    this.socket?.close();
    this.socket = null;
    this.shouldRetry = true;
    this.run();
  }

  stop() {
    this.shouldRetry = false;
    this.socket?.close();
  }

  send(msg: string | ArrayBuffer) {
    this.socket?.send(msg);
  }

  async run() {
    if (this.closed) { return; }
    this.shouldRetry = true
    let retryTime = INIT_TIME;

    while (this.shouldRetry) {
      let wasOpen = false;

      try {
        this.closed = this.openSocket();
        wasOpen = await this.closed; // wait here forever, I hope
      } catch (err) {
        wasOpen = false;
        console.error("Error opening socket:", err, "UnauthError:", err instanceof UnauthError);
      } finally {
        this.closed = null;
      }

      if (wasOpen) {
        retryTime = INIT_TIME;
      } else if (retryTime < 1200000) {
        retryTime += 30000;
      }

      this.socket = null;
      if (this.shouldRetry) {
        //console.log("waiting to restart:", retryTime);
        await bluebird.delay(retryTime);
        //console.log("restarting socket");
      } else {
        //console.log("stopping web socket");
      }

    }
  }

  openSocket(): Promise<boolean> {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    if (!this.deviceID) {
      console.log("no device ID set on socket service");
      return bluebird.reject(new Error("No Device ID"));
    }

    // websocket() can throw if connection failed
    return (this.httpService || http).websocket(this.deviceID).then(sock => {
      this.socket = sock;

      return new bluebird<boolean>((resolve, reject) => {
        if (!this.socket) { return reject(new Error("Just making TypeScript happy")); }

        let opened: Promise<any>;
        let wasOpen = false;
        this.socket.onopen = () => {
          //console.log("socket opened:", evt);
          opened = this.msgDelegate?.onOpen() || bluebird.resolve();
          wasOpen = true;
        };
        this.socket.onclose = (event: CloseEvent) => {
          if (!event.code || event.code !== 1000 && event.code !== 1001 && event.code !== 1005) {
            console.log("web socket closed:", event.reason, event);
          }
          this.socket = null;
          this.msgDelegate?.onClose();
          resolve(wasOpen);
        };

        this.socket.onmessage = async (event: MessageEvent) => {
          let frame: MessageFrame;
          try {
            await opened;
            if (event.data instanceof Blob || event.data instanceof Buffer) {
              frame = deserialize(new Uint8Array(event.data instanceof Blob ? await event.data.arrayBuffer() : event.data),
                { promoteBuffers: true, promoteValues: true }) as MessageFrame;
            } else {
              frame = JSON.parse(event.data);
            }
            this.processMessage(frame);
          } catch (err) {
            console.error("Websocket error:", err, event);
          }
        };

        this.socket.onerror = (event: Event) => {
          console.error("Error from websocket:", event);
          this.socket?.close();
        };
      });
    });
  }

  protected processMessage(cmd: MessageFrame | MeetingMessageFrame) {
    switch (cmd.cmd) {
      case MessageFrameType.ACK_OPEN:
        break;
      case MessageFrameType.EXPIRED_TOKEN:
        this.shouldRetry = false;
        break;
      default:
        this.msgDelegate?.processMessage(cmd);
    }
  }
}

let socketService: SocketService;

export function socketServiceFactory(): SocketService {
  if (!socketService) {
    socketService = new SocketServiceImpl();
  }
  return socketService;
}

export function socketServiceTestFactory(httpService: HttpService): SocketService {
  return new SocketServiceImpl(httpService)
}
